/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'
import { css } from '@emotion/react'
import { useHistory, useLocation } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { font } from 'src/components/css'
import Icon from 'src/components/Icon'
import SchedulerWizard from '../SchedulerWizard'

interface ScheduleSubTabsProps {
  scheduleId: string
  isSchedulerWizardEnabled: boolean
  isAdmin: boolean
  listContent: React.ReactNode
  onSchedulePublished?: () => void
}

type SubTabId = 'list' | 'admin'

interface SubTab {
  id: SubTabId
  label: string
  icon: string
}

const SUB_TABS: SubTab[] = [
  { id: 'list', label: 'Live', icon: 'th-list' },
  { id: 'admin', label: 'Scheduler Admin', icon: 'cog' },
]

const containerStyles = css`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: visible;
`

const getTabStyles = (isActive: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  height: 33.6px;
  border: none;
  background: ${isActive ? colors.SECONDARY_300 : colors.SECONDARY_500};
  cursor: pointer;
  transition: background 0.15s ease;

  &:first-of-type {
    border-radius: 8px 0 0 8px;
  }

  &:last-of-type {
    border-radius: 0 8px 8px 0;
  }

  &:hover {
    background: ${isActive ? colors.SECONDARY_300 : colors.SECONDARY_400};
  }
`

const getTabLabelStyles = (isActive: boolean) => css`
  ${font.body}
  font-size: 13px;
  font-weight: ${isActive ? 600 : 400};
  line-height: 1;
  color: ${isActive ? colors.WHITE : colors.NEUTRAL_50};
  margin: 0;
  white-space: nowrap;
`

const segmentedControlWrapperStyles = css`
  position: relative;
  display: inline-flex;
`

const betaTagStyles = css`
  position: absolute;
  top: -8px;
  right: -4px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
`

const betaBadgeStyles = css`
  background: ${colors.PRIMARY_300};
  padding: 2px 4px;
  border-radius: 2px 2px 0 2px;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 500;
  line-height: 1;
  color: ${colors.SECONDARY_300};
  white-space: nowrap;
`

const betaArrowStyles = css`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid ${colors.PRIMARY_300};
`

const ScheduleSubTabs: React.FC<ScheduleSubTabsProps> = ({
  scheduleId,
  isSchedulerWizardEnabled,
  isAdmin,
  listContent,
  onSchedulePublished,
}) => {
  const history = useHistory()
  const location = useLocation()

  const activeTab = useMemo<SubTabId>(() => {
    const params = new URLSearchParams(location.search)
    const view = params.get('view')
    if (view === 'admin') {
      return 'admin'
    }
    return 'list'
  }, [location.search])

  const showSubTabs = isSchedulerWizardEnabled && isAdmin

  const handleTabClick = (tabId: SubTabId) => {
    if (tabId === activeTab) {
      return
    }
    const params = new URLSearchParams(location.search)
    params.set('view', tabId)
    history.replace({ search: params.toString() })
  }

  // When feature flag is off or not admin, just render list content directly
  if (!showSubTabs) {
    return listContent as React.ReactElement
  }

  // Build the segmented control element
  const segmentedControl = (
    <div css={segmentedControlWrapperStyles}>
      <div css={containerStyles}>
        {SUB_TABS.map((tab) => {
          const isActive = tab.id === activeTab
          return (
            <button
              key={tab.id}
              type='button'
              onClick={() => handleTabClick(tab.id)}
              css={getTabStyles(isActive)}
            >
              <Icon
                name={tab.icon}
                fontSize={16}
                color={isActive ? colors.WHITE : colors.NEUTRAL_50}
              />
              <p css={getTabLabelStyles(isActive)}>{tab.label}</p>
            </button>
          )
        })}
      </div>
      <div css={betaTagStyles}>
        <div css={betaBadgeStyles}>BETA</div>
        <div css={betaArrowStyles} />
      </div>
    </div>
  )

  return (
    <div>
      {/* List view: segmented control standalone + list content */}
      {activeTab === 'list' && (
        <>
          <div
            css={css`
              padding: 24px 0 16px;
            `}
          >
            {segmentedControl}
          </div>
          {listContent}
        </>
      )}

      {/* Admin view: pass segmented control into SchedulerWizard for unified nav bar */}
      {activeTab === 'admin' && (
        <SchedulerWizard
          competitionIdProp={scheduleId}
          subTabsControl={segmentedControl}
          onSchedulePublished={onSchedulePublished}
        />
      )}
    </div>
  )
}

export default ScheduleSubTabs
